import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { SitesSelectorComponent } from 'src/app/shared/sites-selector/sites-selector.component';
import { ApiDataSource } from '../../../utils/api-data-source';
import { ApiRequestService } from '../../../shared/api-request.service';
import { MatSort, Sort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';

@Component({
  selector: 'app-notification-roles-sites',
  templateUrl: './notification-roles-sites.component.html',
  styleUrls: ['./notification-roles-sites.component.scss']
})
export class NotificationRolesSitesComponent implements OnInit, AfterViewInit {

  // Get the notification role id.
  @Input('notification_role_id') notification_role_id: number;

  // Columns to show and the data source.
  displayedColumns: string[] = ['select', 'name', 'site_type', 'site_status', 'site_risk_assessment', 'actions'];
  dataSource: NotificationRoleSitesDataSource = new NotificationRoleSitesDataSource(this.app, this.api, true);

  // The paginator and sorter.
  @ViewChild(MatPaginator, {static: false}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: false}) sort: MatSort;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService
  ) {}

  ngOnInit(): void {
    // Store the notification role id before making API requests.
    this.dataSource.notification_role_id = this.notification_role_id;

    // Make the initial API request to get the list of sites.
    this.dataSource.getData(true);
  }

  ngAfterViewInit(): void {
    // Reset the paginator when sorting.
    this.sort.sortChange.subscribe((): void => {
      this.paginator.pageIndex = 0;
    });

    const _tap: any = tap((): void => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order_by = this.sort.active;
      this.dataSource.order = this.sort.direction;

      // Refresh the data.
      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator.page.pipe(_tap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange.pipe(tap((sorter: Sort): void => {
      this.dataSource.order = sorter['direction'];
      this.dataSource.getData(true);
    })).subscribe();
  }

  /**
   * Opens the site selector dialog and syncs selected sites with the notification role.
   *
   * @returns {void}
   */
  onSelectSites(): void {
    // Show the site selector.
    this.utils.showComponentDialog(SitesSelectorComponent, {
        multiple: true,
        selected: this.dataSource.allIds
      }, {
        width: '1200px'
      }, (site_ids: number[]): void => {
        if ( typeof site_ids !== 'undefined') {
          // Make an update request to the API.
          this.api.makeRequest('put', `v2/account/notification-roles/${this.notification_role_id}/sites`, {
            site_ids: site_ids
          }).then((): void => {
            this.utils.showToast('The selected ' + this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase() + ' were synced successfully to the notification role.');
          }).finally((): void => {
            this.dataSource.getData(true);
          });
        }
      }
    );
  }

  /**
   * Method to link all sites to the notification role.
   * It prompts the user with a modal to confirm the action before making an API request to link sites.
   *
   * @return {void}
   */
  onLinkAllSites(): void {
    // Make a request to link all sites to the notification role.
    this.utils.showModal('Link All Sites', `Are you sure you want to link all ${this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase()} to the notification role?`, (): void => {
      // Make an update request to the API.
      this.api.makeRequest('put', `v2/account/notification-roles/${this.notification_role_id}/sites`, {
        site_ids: 'all'
      }).then((): void => {
        this.utils.showToast('The selected ' + this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase() + ' were synced successfully to the notification role.');
      }).finally((): void => {
        this.dataSource.getData(true);
      });
    });
  }

  /**
   * Unlinks a site from the notification role.
   *
   * @param {number} site_id - The ID of the site to unlink.
   *
   * @param evt
   * @return {void}
   */
  onUnlinkSite(site_id: number, evt: any): void {
    this.utils.showQuickActions(evt.target, 'Are you sure you want to unlink the ' + this.utils.getLangTerm('parent-child-sites-combined.singular', 'Site').toLowerCase() + ' from the notification role?', [
      {
        text: 'Yes',
        handler: (): void => {
          // Unlink the site.
          this.unlinkSites([site_id]);
        }
      },
      {
        text: 'No',
        handler: (): void => { }
      }
    ]);
  }

  /**
   * Unlinks selected sites from the notification role.
   *
   * @return {void}
   */
  onUnlinkSelectedSites(): void {
    // Get the selected notification role ids.
    const ids: any[] = this.dataSource.selection.selected;
    // Check if we have notification roles selected.
    if ( ids.length == 0 ) {
      this.utils.showModal('Unlink Sites', 'Please select some ' + this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase() + ' to unlink from the notification role.');
      return;
    }
    // Make a request to delete the notification roles.
    this.utils.showModal('Unlink Sites', `Are you sure you want to unlink (${ids.length}) ${this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase()} from the notification role?`, (): void => {
      this.unlinkSites(this.dataSource.selection.selected).then((): void => {
        // Clear the list selection.
        this.dataSource.selection.clear();
      });
    });
  }

  /**
   * Makes a request to unlink all sites from the notification role and clears the list selection.
   *
   * @return {void}
   */
  onUnlinkAllSites(): void {
    // Make a request to delete the notification roles.
    this.utils.showModal('Unlink All Sites', `Are you sure you want to unlink all ${this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase()} from the notification role?`, (): void => {
      this.unlinkSites('all').then((): void => {
        // Clear the list selection.
        this.dataSource.selection.clear();
      });
    });
  }

  /**
   * This method unlinks the specified sites from the notification role.
   *
   * @param {number[]} site_ids - An array of site IDs to unlink from the notification role.
   * @returns {Promise<any>} - A promise that resolves when the unlinking is complete.
   * @private
   */
  private unlinkSites(site_ids: number[]|string): Promise<any> {
    // Make an API request to unlink sites.
    return this.api.makeRequest('delete', `v2/account/notification-roles/${this.notification_role_id}/sites`, {}, {
      site_ids: typeof site_ids == 'string' ? site_ids : site_ids.join(',')
    }).then((): void => {
      this.utils.showToast('The ' + this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase() + ' were detached from the notification role.');
      this.dataSource.getData();
    });
  }
}

export class NotificationRoleSitesDataSource extends ApiDataSource {

  // Order and order by params.
  order_by: string = 'name';
  order: string = 'asc';

  // Store the notification role id.
  notification_role_id: number;

  getData(resetOffset: boolean = false): void {
    this.makeRequest(`v2/account/notification-roles/${this.notification_role_id}/sites`, resetOffset, {
      order_by: this.order_by,
      order: this.order
    });
  }
}
